import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Save, MapPin, AlertTriangle, Sparkles } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { listMyVenues, setEventVenue } from "@/lib/venues.functions";
import { getAuthHeaders } from "@/lib/auth-helper";

type Venue = { id: string; name: string; city: string; venue_maps: { id: string }[] };
type Zone = { id: string; name: string; zone_type: string; color: string; capacity_total: number | null };
type ZoneSetting = { price: number; maxPerUser: number; isVisible: boolean; resaleEnabled: boolean };

export const Route = createFileRoute("/dashboard/events/$eventId/venue")({
  head: () => ({ meta: [{ title: "Asignar venue al evento — ZYNTRA" }] }),
  component: AssignVenuePage,
});

function AssignVenuePage() {
  const { eventId } = Route.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState<{ id: string; title: string; venue_id: string | null; venue_map_id: string | null } | null>(null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [selectedVenueId, setSelectedVenueId] = useState<string>("");
  const [zones, setZones] = useState<Zone[]>([]);
  const [settings, setSettings] = useState<Record<string, ZoneSetting>>({});
  // Tracks which zones already had a row in event_zone_settings on load.
  // Anything NOT in this set is "nueva sin configurar" and shown as a warning.
  const [persistedZoneIds, setPersistedZoneIds] = useState<Set<string>>(new Set());
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
    if (user) loadInitial();
  }, [user, loading, eventId]);

  async function loadInitial() {
    setLoadingData(true);
    try {
      const { data: ev } = await supabase
        .from("events")
        .select("id, title, organizer_id, venue_id, venue_map_id")
        .eq("id", eventId)
        .single();
      if (!ev || ev.organizer_id !== user?.id) {
        navigate({ to: "/dashboard" });
        return;
      }
      setEvent(ev);

      const headers = await getAuthHeaders();
      const result = await listMyVenues({ headers } as any);
      setVenues((result.venues || []) as any);

      if (ev.venue_id) {
        setSelectedVenueId(ev.venue_id);
        await loadZones(ev.venue_id, ev.id);
      }
    } finally {
      setLoadingData(false);
    }
  }

  async function loadZones(venueId: string, evId: string) {
    const { data: maps } = await supabase.from("venue_maps").select("id").eq("venue_id", venueId).limit(1);
    const map = maps?.[0];
    if (!map) {
      setZones([]);
      return;
    }
    const { data: zoneData } = await supabase
      .from("venue_zones")
      .select("id, name, zone_type, color, capacity_total")
      .eq("venue_map_id", map.id)
      .eq("is_active", true)
      .order("sort_order");
    setZones(zoneData || []);

    const { data: existing } = await supabase
      .from("event_zone_settings")
      .select("zone_id, price, max_per_user, is_visible, resale_enabled")
      .eq("event_id", evId);
    const map2: Record<string, ZoneSetting> = {};
    const persisted = new Set<string>();
    for (const z of zoneData || []) {
      const ex = existing?.find((s) => s.zone_id === z.id);
      if (ex) persisted.add(z.id);
      map2[z.id] = {
        // New zones default to NOT visible so they don't appear unpriced
        price: ex ? Number(ex.price) : 0,
        maxPerUser: ex?.max_per_user ?? 4,
        isVisible: ex ? ex.is_visible : false,
        resaleEnabled: ex?.resale_enabled ?? true,
      };
    }
    setSettings(map2);
    setPersistedZoneIds(persisted);
  }

  // Quickly mark all unconfigured zones as visible (price stays 0 — organizer
  // still needs to set it, but at least they're surfaced in the form).
  function autoCompleteUnconfigured() {
    setSettings((prev) => {
      const copy = { ...prev };
      for (const z of zones) {
        if (!persistedZoneIds.has(z.id)) {
          copy[z.id] = { ...copy[z.id], isVisible: true };
        }
      }
      return copy;
    });
    setSuccess("Zonas marcadas como visibles. Asigná el precio y guardá.");
  }

  async function onVenueChange(vId: string) {
    setSelectedVenueId(vId);
    if (vId) await loadZones(vId, eventId);
    else setZones([]);
  }

  function updateSetting(zoneId: string, patch: Partial<ZoneSetting>) {
    setSettings((prev) => ({ ...prev, [zoneId]: { ...prev[zoneId], ...patch } }));
  }

  async function save() {
    if (!selectedVenueId) {
      setError("Seleccioná un venue");
      return;
    }
    const venue = venues.find((v) => v.id === selectedVenueId);
    const mapId = venue?.venue_maps?.[0]?.id;
    if (!mapId) {
      setError("El venue no tiene mapa configurado");
      return;
    }
    if (zones.length === 0) {
      setError("Este venue no tiene zonas. Configurá zonas primero.");
      return;
    }
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      await setEventVenue({
        data: {
          eventId,
          venueId: selectedVenueId,
          venueMapId: mapId,
          zoneSettings: zones.map((z) => ({
            zoneId: z.id,
            salesMode: z.zone_type === "seated" ? ("seated" as const) : ("general" as const),
            price: settings[z.id]?.price ?? 0,
            maxPerUser: settings[z.id]?.maxPerUser ?? 4,
            isVisible: settings[z.id]?.isVisible ?? true,
            resaleEnabled: settings[z.id]?.resaleEnabled ?? true,
          })),
        },
        headers,
      } as any);
      setSuccess("Venue asignado al evento");
    } catch (e: any) {
      setError(e.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  if (loading || loadingData) {
    return <div className="mx-auto max-w-5xl px-4 pt-24 pb-20 text-muted-foreground">Cargando...</div>;
  }

  return (
    <div className="mx-auto max-w-5xl px-4 pt-24 pb-20 sm:px-6">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Volver al dashboard
      </Link>

      <div className="mb-6">
        <h1 className="text-2xl font-black text-foreground">Asignar venue</h1>
        <p className="mt-1 text-sm text-muted-foreground">{event?.title}</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 mb-6">
        <label className="mb-2 block text-xs font-semibold text-muted-foreground">Seleccioná un venue</label>
        <select
          value={selectedVenueId}
          onChange={(e) => onVenueChange(e.target.value)}
          className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none"
        >
          <option value="">— Sin venue —</option>
          {venues.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name} · {v.city}
            </option>
          ))}
        </select>
        {venues.length === 0 && (
          <p className="mt-2 text-xs text-muted-foreground">
            No tenés venues.{" "}
            <Link to="/dashboard/venues/new" className="text-primary hover:underline">
              Crear uno
            </Link>
          </p>
        )}
      </div>

      {zones.length > 0 && (
        <>
            {zones.filter((z) => !persistedZoneIds.has(z.id)).length > 0 && (
              <div className="mb-4 rounded-2xl border border-warning/40 bg-warning/10 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-foreground">
                      {zones.filter((z) => !persistedZoneIds.has(z.id)).length} zona{zones.filter((z) => !persistedZoneIds.has(z.id)).length === 1 ? "" : "s"} sin configuración de venta
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Estas zonas existen en el venue pero no se mostrarán al público hasta que les asignes precio y las marques visibles:{" "}
                      <span className="font-medium text-foreground">
                        {zones.filter((z) => !persistedZoneIds.has(z.id)).map((z) => z.name).join(", ")}
                      </span>
                    </p>
                    <button
                      onClick={autoCompleteUnconfigured}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-warning/20 px-3 py-1.5 text-xs font-bold text-warning hover:bg-warning/30 transition-colors"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Marcar todas como visibles
                    </button>
                  </div>
                </div>
              </div>
            )}
            <h2 className="mb-3 text-lg font-bold text-foreground">Reglas comerciales por zona</h2>
            <div className="space-y-3">
              {zones.map((z) => {
                const s = settings[z.id] || { price: 0, maxPerUser: 4, isVisible: true, resaleEnabled: true };
                const isUnconfigured = !persistedZoneIds.has(z.id);
                return (
                  <div
                    key={z.id}
                    className={`rounded-2xl border p-5 ${isUnconfigured ? "border-warning/50 bg-warning/5" : "border-border bg-card"}`}
                  >
                    <div className="mb-3 flex items-center gap-3 flex-wrap">
                      <span className="h-4 w-4 rounded" style={{ backgroundColor: z.color }} />
                      <h3 className="font-bold text-card-foreground">{z.name}</h3>
                      {isUnconfigured && (
                        <span className="rounded-full bg-warning/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warning">
                          Sin configurar
                        </span>
                      )}
                      <span className="ml-auto text-xs text-muted-foreground">
                        <MapPin className="inline h-3 w-3" /> Capacidad {z.capacity_total || 0}
                      </span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-[10px] font-semibold uppercase text-muted-foreground">Precio (CRC) *</label>
                      <input
                        type="number"
                        min={0}
                        value={s.price}
                        onChange={(e) => updateSetting(z.id, { price: Number(e.target.value) })}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] font-semibold uppercase text-muted-foreground">Máx por usuario</label>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={s.maxPerUser}
                        onChange={(e) => updateSetting(z.id, { maxPerUser: Number(e.target.value) })}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-foreground sm:pt-6">
                      <input
                        type="checkbox"
                        checked={s.isVisible}
                        onChange={(e) => updateSetting(z.id, { isVisible: e.target.checked })}
                        className="h-4 w-4 accent-primary"
                      />
                      Visible al público
                    </label>
                    <label className="flex items-center gap-2 text-xs text-foreground sm:pt-6">
                      <input
                        type="checkbox"
                        checked={s.resaleEnabled}
                        onChange={(e) => updateSetting(z.id, { resaleEnabled: e.target.checked })}
                        className="h-4 w-4 accent-primary"
                      />
                      Permite reventa
                    </label>
                  </div>
                  </div>
                );
              })}
            </div>
          </>
      )}

      {error && <p className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
      {success && <p className="mt-4 rounded-lg bg-success/10 px-3 py-2 text-sm text-success">{success}</p>}

      <button
        onClick={save}
        disabled={saving || !selectedVenueId || zones.length === 0}
        className="mt-6 inline-flex items-center gap-2 rounded-xl gradient-primary px-5 py-3 text-sm font-bold text-primary-foreground glow-primary disabled:opacity-50"
      >
        <Save className="h-4 w-4" />
        {saving ? "Guardando..." : "Guardar configuración"}
      </button>
    </div>
  );
}
